import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Stack, Typography, Paper, Chip, Divider,
    Button, IconButton, CircularProgress, Alert, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CreateApi from '../../../shared/Settings/appSettings';
import { StyledTextboxInput } from '../../../shared/Components/StyledTextboxInput';
import { StyledAutocompleteInput } from '../../../shared/Components/StyledAutocompleteInput';
import { useTBContext } from '../../Contexts/useTBStates';
import stateAbbreviations from '../../../shared/Settings/StateAbbreviations.json';
import type { MomentUser, UpdateUserProfilePayload, UserTourAssociation } from '@moment-golf/types';
import { useAuth } from 'react-oidc-context';

interface UserProfileSectionProps {
    appDims: any;
    compHeights: any;
}

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

// ── Series search result shape returned by /um/SearchTours ───────────────────
type TourSearchResult = { _id: string; groupName: string; groupType: string };

// ── Series card ──────────────────────────────────────────────────────────────
interface SeriesCardProps {
    user: MomentUser;
    factor: number;
    appTheme: any;
    api: any;
    auth: any;
    onUserUpdated: (u: MomentUser) => void;
}

const SeriesCard = ({ user, factor, appTheme, api, auth, onUserUpdated }: SeriesCardProps) => {
    const f = factor;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [searchOptions, setSearchOptions] = useState<TourSearchResult[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const [removing, setRemoving] = useState<string | null>(null);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const tours: UserTourAssociation[] = user.tours ?? [];

    const handleSearchInput = useCallback((inputValue: string) => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        if (!inputValue || inputValue.trim().length < 1) {
            setSearchOptions([]);
            return;
        }
        debounceTimer.current = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const res = await api.get(`/um/SearchTours?name=${encodeURIComponent(inputValue.trim())}`);
                setSearchOptions(res.data ?? []);
            } catch {
                setSearchOptions([]);
            } finally {
                setSearchLoading(false);
            }
        }, 350);
    }, [api]);

    const handleAdd = useCallback(async (selected: TourSearchResult | null) => {
        if (!selected) return;
        setAdding(true);
        setAddError(null);
        try {
            await api.post('/um/AddUserTour', {
                userID: user.userID,
                tourID: selected._id,
                tourName: selected.groupName,
                groupType: selected.groupType,
                modifiedBy: auth.user?.profile?.sub ?? 'admin',
            });
            const newAssoc: UserTourAssociation = {
                tourID: selected._id,
                tourName: selected.groupName,
                groupType: selected.groupType,
                joinedAt: new Date(),
            };
            onUserUpdated({ ...user, tours: [...tours, newAssoc] });
            setDialogOpen(false);
            setSearchOptions([]);
        } catch {
            setAddError('Failed to add series. Please try again.');
        } finally {
            setAdding(false);
        }
    }, [api, auth, tours, user, onUserUpdated]);

    const handleRemove = useCallback(async (tourID: string) => {
        setRemoving(tourID);
        try {
            await api.delete('/um/RemoveUserTour', {
                data: { userID: user.userID, tourID },
            });
            onUserUpdated({ ...user, tours: tours.filter(t => t.tourID !== tourID) });
        } catch {
            // silently ignore — user can retry
        } finally {
            setRemoving(null);
        }
    }, [api, tours, user, onUserUpdated]);

    return (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <EmojiEventsIcon color="primary" sx={{ fontSize: 16 }} />
                    <Typography
                        variant="caption"
                        color="primary.main"
                        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 * f }}
                    >
                        Series
                    </Typography>
                </Stack>
                <IconButton size="small" onClick={() => { setAddError(null); setSearchOptions([]); setDialogOpen(true); }}>
                    <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Stack>

            {tours.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 * f }}>
                    No series associations on record.
                </Typography>
            ) : (
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {tours.map(t => (
                        <Chip
                            key={t.tourID}
                            label={t.tourName || t.tourID}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onDelete={removing === t.tourID ? undefined : () => handleRemove(t.tourID)}
                            deleteIcon={removing === t.tourID ? <CircularProgress size={12} /> : undefined}
                            sx={{ fontSize: 11 * f }}
                        />
                    ))}
                </Stack>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 14 * f }}>Add Series</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ mt: 2, mb: 1 }}>
                        {addError && <Alert severity="error">{addError}</Alert>}
                        <StyledAutocompleteInput
                            label="Search series by name"
                            factor={f}
                            appTheme={appTheme}
                            options={searchOptions}
                            loading={searchLoading}
                            getOptionLabel={(opt: TourSearchResult) => opt.groupName}
                            isOptionEqualToValue={(opt: TourSearchResult, val: TourSearchResult) => opt._id === val._id}
                            filterOptions={(x: TourSearchResult[]) => x}
                            onInputChange={(_: any, val: string) => handleSearchInput(val)}
                            onChange={(_: any, val: TourSearchResult | null) => handleAdd(val)}
                            noOptionsText="Type to search series"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={adding} color="inherit">Cancel</Button>
                    {adding && <CircularProgress size={20} sx={{ mr: 1 }} />}
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

// ── Small read-only label+value display ─────────────────────────────────────
const ReadOnlyField = ({ label, value, factor }: { label: string; value: string; factor: number }) => (
    <Stack direction="column" spacing={0.25} sx={{ minWidth: 160 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 * factor, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 12 * factor, fontWeight: 500 }}>
            {value || '—'}
        </Typography>
    </Stack>
);

// ── Section sub-header ───────────────────────────────────────────────────────
const SectionLabel = ({ label, factor }: { label: string; factor: number }) => (
    <Typography
        variant="caption"
        color="primary.main"
        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 * factor }}
    >
        {label}
    </Typography>
);

export const UserProfileSection = (_props: UserProfileSectionProps) => {
    const { appTheme, factor, selectedUser, setSelectedUser, optionsLoaded } = useTBContext();
    const api = CreateApi();
    const auth = useAuth();

    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [draft, setDraft] = useState<MomentUser | null>(null);

    // Sync draft when selectedUser changes or edit starts
    useEffect(() => {
        if (selectedUser) setDraft({ ...selectedUser });
    }, [selectedUser]);

    const f = factor < 1 ? factor : 1;

    // ── Reference option lookups ─────────────────────────────────────────────
    const membershipOptions: any[] = (optionsLoaded as any[]).find((o: any) => o.keyName === 'MembershipTypes')?.options ?? [];
    const accountTypeOptions: any[] = (optionsLoaded as any[]).find((o: any) => o.keyName === 'AccountType')?.options ?? [];
    const accountAccessOptions: any[] = (optionsLoaded as any[]).find((o: any) => o.keyName === 'AccountAccessType')?.options ?? [];

    const setDraftField = (field: keyof MomentUser, value: any) => {
        setDraft(prev => prev ? { ...prev, [field]: value } : prev);
    };

    const handleCancel = () => {
        setDraft(selectedUser ? { ...selectedUser } : null);
        setSaveError(null);
        setEditing(false);
    };

    const handleSave = useCallback(async () => {
        if (!draft) return;
        setSaving(true);
        setSaveError(null);
        try {
            const payload: UpdateUserProfilePayload = {
                userID: draft.userID,
                firstName: draft.firstName,
                lastName: draft.lastName,
                displayName: draft.displayName,
                membershipTier: draft.membershipTier,
                membershipType: draft.membershipType,
                accountTier: draft.accountTier,
                accountType: draft.accountType,
                phoneNumber: draft.phoneNumber,
                birthDate: draft.birthDate,
                address: draft.address,
                city: draft.city,
                state: draft.state,
                zipCode: draft.zipCode,
                gender: draft.gender,
                ghinNumber: draft.ghinNumber,
                modifiedBy: auth.user?.profile?.sub ?? 'admin',
            };
            await api.put('/um/UpdateUserProfile', payload);
            setSelectedUser(draft);
            setEditing(false);
        } catch {
            setSaveError('Failed to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    }, [api, auth, draft, setSelectedUser]);

    if (!selectedUser || !draft) return null;

    /** Render any date value in UTC to avoid timezone-induced day shifts. */
    const formatDate = (val: any) => {
        if (!val) return '—';
        try { return new Date(val).toLocaleDateString('en-US', { timeZone: 'UTC' }); } catch { return String(val); }
    };

    /** Return the UTC calendar date as a YYYY-MM-DD string for <input type="date"> bindings. */
    const toUTCDateInput = (val: any) => {
        if (!val) return '';
        try { return new Date(val).toISOString().slice(0, 10); } catch { return ''; }
    };

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                px: 2,
                py: 1.5,
                boxSizing: 'border-box',
            }}
        >
            {/* ── Header (sticky in layout — cards scroll underneath) ─────── */}
            <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" color="primary.main" sx={{ fontWeight: 600, fontSize: 14 * f }}>
                        User Profile
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {!editing ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => setEditing(true)}
                                sx={{ fontSize: 11 * f }}
                            >
                                Edit
                            </Button>
                        ) : (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                    disabled={saving}
                                    sx={{ fontSize: 11 * f }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                    onClick={handleSave}
                                    disabled={saving}
                                    sx={{ fontSize: 11 * f }}
                                >
                                    {saving ? 'Saving…' : 'Save'}
                                </Button>
                            </>
                        )}
                    </Stack>
                </Stack>

                {saveError && <Alert severity="error" sx={{ mt: 1.5 }}>{saveError}</Alert>}
            </Box>

            {/* ── Scrollable cards ─────────────────────────────────────────── */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                }}
            >
                <Stack direction="column" spacing={2} sx={{ pb: 0.5 }}>

                {/* ── Identity (read-only) ─────────────────────────────────── */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <SectionLabel label="Identity" factor={f} />
                    <Stack direction="row" flexWrap="wrap" gap={2} mt={1}>
                        <ReadOnlyField label="User ID" value={selectedUser.userID} factor={f} />
                        <ReadOnlyField label="Email" value={selectedUser.userEmail} factor={f} />
                        <ReadOnlyField label="Created" value={formatDate(selectedUser.createdAt)} factor={f} />
                        <ReadOnlyField label="Modified" value={formatDate(selectedUser.modifiedAt)} factor={f} />
                        <ReadOnlyField label="Created By" value={selectedUser.createdBy} factor={f} />
                        <ReadOnlyField label="Modified By" value={selectedUser.modifiedBy} factor={f} />
                    </Stack>
                </Paper>

                {/* ── Name ────────────────────────────────────────────────── */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <SectionLabel label="Name" factor={f} />
                    <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1}>
                        {editing ? (
                            <>
                                <StyledTextboxInput
                                    label="First Name"
                                    value={draft.firstName}
                                    onChange={e => setDraftField('firstName', e.target.value)}
                                    factor={f}
                                    appTheme={appTheme}
                                    sx={{ flex: 1, minWidth: 160 }}
                                />
                                <StyledTextboxInput
                                    label="Last Name"
                                    value={draft.lastName}
                                    onChange={e => setDraftField('lastName', e.target.value)}
                                    factor={f}
                                    appTheme={appTheme}
                                    sx={{ flex: 1, minWidth: 160 }}
                                />
                                <StyledTextboxInput
                                    label="Display Name"
                                    value={draft.displayName}
                                    onChange={e => setDraftField('displayName', e.target.value)}
                                    factor={f}
                                    appTheme={appTheme}
                                    sx={{ flex: 1, minWidth: 200 }}
                                />
                            </>
                        ) : (
                            <>
                                <ReadOnlyField label="First Name" value={selectedUser.firstName} factor={f} />
                                <ReadOnlyField label="Last Name" value={selectedUser.lastName} factor={f} />
                                <ReadOnlyField label="Display Name" value={selectedUser.displayName} factor={f} />
                            </>
                        )}
                    </Stack>
                </Paper>

                {/* ── Account & Membership ─────────────────────────────────── */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <SectionLabel label="Account & Membership" factor={f} />
                    <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1}>
                        {editing ? (
                            <>
                                {/* membershipType — drives membershipTier automatically */}
                                <Box sx={{ flex: 1, minWidth: 180 }}>
                                    <StyledAutocompleteInput
                                        label="Membership Type"
                                        factor={f}
                                        appTheme={appTheme}
                                        options={membershipOptions}
                                        value={membershipOptions.find((o: any) => o.type === draft.membershipType) ?? null}
                                        getOptionLabel={(o: any) => `${o.type} (Tier ${o.tier})`}
                                        isOptionEqualToValue={(o: any, v: any) => o.type === v?.type}
                                        onChange={(_: any, val: any) => {
                                            if (val) {
                                                setDraftField('membershipType', val.type);
                                                setDraftField('membershipTier', val.tier);
                                            }
                                        }}
                                    />
                                </Box>
                                <Stack direction="column" spacing={0.25} sx={{ minWidth: 100 }} justifyContent="center">
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 * f, textTransform: 'uppercase' }}>
                                        Membership Tier
                                    </Typography>
                                    <Chip
                                        label={`Tier ${draft.membershipTier}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: 11 * f, width: 'fit-content' }}
                                    />
                                </Stack>
                                {/* accountType */}
                                <Box sx={{ flex: 1, minWidth: 180 }}>
                                    <StyledAutocompleteInput
                                        label="Account Type"
                                        factor={f}
                                        appTheme={appTheme}
                                        options={accountTypeOptions}
                                        value={accountTypeOptions.find((o: any) => o.accountType === draft.accountType) ?? null}
                                        getOptionLabel={(o: any) => o.accountType}
                                        isOptionEqualToValue={(o: any, v: any) => o.accountType === v?.accountType}
                                        onChange={(_: any, val: any) => {
                                            if (val) setDraftField('accountType', val.accountType);
                                        }}
                                    />
                                </Box>
                                {/* accountTier — driven by AccountAccessType ref */}
                                <Box sx={{ flex: 1, minWidth: 180 }}>
                                    <StyledAutocompleteInput
                                        label="Account Access Level"
                                        factor={f}
                                        appTheme={appTheme}
                                        options={accountAccessOptions}
                                        value={accountAccessOptions.find((o: any) => o.index === draft.accountTier) ?? null}
                                        getOptionLabel={(o: any) => `${o.type} (Level ${o.index})`}
                                        isOptionEqualToValue={(o: any, v: any) => o.index === v?.index}
                                        onChange={(_: any, val: any) => {
                                            if (val) setDraftField('accountTier', val.index);
                                        }}
                                    />
                                </Box>
                            </>
                        ) : (
                            <>
                                <ReadOnlyField label="Membership Type" value={selectedUser.membershipType} factor={f} />
                                <ReadOnlyField label="Membership Tier" value={`Tier ${selectedUser.membershipTier}`} factor={f} />
                                <ReadOnlyField label="Account Type" value={selectedUser.accountType} factor={f} />
                                <ReadOnlyField label="Account Access Level" value={`Tier ${selectedUser.accountTier}`} factor={f} />
                            </>
                        )}
                    </Stack>
                </Paper>

                {/* ── Contact Info ─────────────────────────────────────────── */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <SectionLabel label="Contact Information" factor={f} />
                    <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1}>
                        {editing ? (
                            <>
                                <StyledTextboxInput
                                    label="Phone Number"
                                    value={draft.phoneNumber}
                                    onChange={e => setDraftField('phoneNumber', e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                                    factor={f}
                                    appTheme={appTheme}
                                    inputProps={{ type: 'tel', autoComplete: 'tel' }}
                                    helperText={draft.phoneNumber && draft.phoneNumber.length !== 10 ? 'Must be 10 digits' : undefined}
                                    error={!!draft.phoneNumber && draft.phoneNumber.length !== 10}
                                    sx={{ flex: 1, minWidth: 180 }}
                                />
                                <StyledTextboxInput
                                    label="Birth Date"
                                    value={toUTCDateInput(draft.birthDate)}
                                    onChange={e => setDraftField('birthDate', e.target.value)}
                                    type="date"
                                    factor={f}
                                    appTheme={appTheme}
                                    inputProps={{ autoComplete: 'bday' }}
                                    sx={{ flex: 1, minWidth: 180 }}
                                />
                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                    <StyledAutocompleteInput
                                        label="Gender"
                                        factor={f}
                                        appTheme={appTheme}
                                        options={GENDER_OPTIONS}
                                        value={draft.gender && GENDER_OPTIONS.includes(draft.gender) ? draft.gender : null}
                                        getOptionLabel={(o: string) => o}
                                        isOptionEqualToValue={(o: string, v: string) => o === v}
                                        onChange={(_: any, val: string | null) => setDraftField('gender', val ?? '')}
                                    />
                                </Box>
                            </>
                        ) : (
                            <>
                                <ReadOnlyField
                                    label="Phone Number"
                                    value={selectedUser.phoneNumber
                                        ? selectedUser.phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
                                        : '—'}
                                    factor={f}
                                />
                                <ReadOnlyField label="Birth Date" value={formatDate(selectedUser.birthDate)} factor={f} />
                                <ReadOnlyField label="Gender" value={selectedUser.gender} factor={f} />
                            </>
                        )}
                    </Stack>
                </Paper>

                {/* ── Address ──────────────────────────────────────────────── */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <SectionLabel label="Address" factor={f} />
                    <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1}>
                        {editing ? (
                            <>
                                <StyledTextboxInput
                                    label="Street Address"
                                    value={draft.address}
                                    onChange={e => setDraftField('address', e.target.value)}
                                    factor={f}
                                    appTheme={appTheme}
                                    inputProps={{ autoComplete: 'street-address' }}
                                    sx={{ flex: 3, minWidth: 360 }}
                                />
                                <StyledTextboxInput
                                    label="City"
                                    value={draft.city}
                                    onChange={e => setDraftField('city', e.target.value)}
                                    factor={f}
                                    appTheme={appTheme}
                                    inputProps={{ autoComplete: 'address-level2' }}
                                    sx={{ flex: 1, minWidth: 160 }}
                                />
                                <Box sx={{ flexShrink: 0, width: 82 }}>
                                    <StyledAutocompleteInput
                                        label="State"
                                        factor={f}
                                        appTheme={appTheme}
                                        options={stateAbbreviations.states}
                                        value={draft.state || null}
                                        getOptionLabel={(o: string) => o}
                                        isOptionEqualToValue={(o: string, v: string) => o === v}
                                        onChange={(_: any, val: string | null) => setDraftField('state', val ?? '')}
                                        autoHighlight
                                    />
                                </Box>
                                <StyledTextboxInput
                                    label="Zip Code"
                                    value={draft.zipCode}
                                    onChange={e => setDraftField('zipCode', e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                                    factor={f}
                                    appTheme={appTheme}
                                    error={!!draft.zipCode && draft.zipCode.length !== 5}
                                    helperText={draft.zipCode && draft.zipCode.length !== 5 ? 'Must be 5 digits' : undefined}
                                    inputProps={{ autoComplete: 'postal-code' }}
                                    sx={{ flex: 1, minWidth: 110 }}
                                />
                            </>
                        ) : (
                            <>
                                <ReadOnlyField label="Street" value={selectedUser.address} factor={f} />
                                <ReadOnlyField label="City" value={selectedUser.city} factor={f} />
                                <ReadOnlyField label="State" value={selectedUser.state} factor={f} />
                                <ReadOnlyField label="Zip Code" value={selectedUser.zipCode} factor={f} />
                            </>
                        )}
                    </Stack>
                </Paper>

                {/* ── Golf Info ────────────────────────────────────────────── */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <SectionLabel label="Golf Information" factor={f} />
                    <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1}>
                        {editing ? (
                            <StyledTextboxInput
                                label="GHIN Number"
                                value={draft.ghinNumber}
                                onChange={e => setDraftField('ghinNumber', e.target.value)}
                                type="number"
                                factor={f}
                                appTheme={appTheme}
                                sx={{ flex: 1, minWidth: 180, maxWidth: 260 }}
                            />
                        ) : (
                            <ReadOnlyField label="GHIN Number" value={selectedUser.ghinNumber} factor={f} />
                        )}
                    </Stack>
                </Paper>

                {/* ── Series ───────────────────────────────────────────────── */}
                <SeriesCard
                    user={selectedUser}
                    factor={f}
                    appTheme={appTheme}
                    api={api}
                    auth={auth}
                    onUserUpdated={setSelectedUser}
                />

                </Stack>
            </Box>
        </Box>
    );
};
